// SmoothMarquee (patched to defer initial layout properly)
export class SmoothMarquee {
    constructor(root, opts = {}) {
        if (!root) throw new Error("root element required");
        this.root = root;
        this.inner = root.querySelector('.marquee__inner');
        if (!this.inner) throw new Error("marquee__inner required");

        // opts
        this.baseSpeed = opts.baseSpeed ?? 80;         // px/sec
        this.maxMultiplier = opts.maxMultiplier ?? 3;  // max speed factor
        this.clampVelTo = opts.clampVelTo ?? 2500;     // px/sec clamp for scroll velocity
        this.direction = opts.direction ?? 1;          // 1 = leftwards, -1 = rightwards
        this.gap = opts.gap ?? 20;                     // px gap between items
        this.recycleBuffer = opts.recycleBuffer ?? 0;  // extra buffer when recycling
        this.springConfig = opts.spring ?? { stiffness: 140, damping: 18 };

        // internals
        this.track = null;
        this.items = [];        // DOM nodes
        this.positions = [];    // numeric x positions (px) relative to container left
        this.sizes = [];        // width (px) of each item
        this._multiplier = 1;
        this._targetMultiplier = 1;
        this._motionAnim = null;
        this._lastFrame = performance.now();
        this._playing = false;
        this._raf = null;

        // scroll velocity tracking
        this._lastScrollY = window.scrollY;
        this._lastScrollTs = performance.now();
        this._measuredVel = 0;

        // motion animate availability
        this.animate = window.motion?.animate ?? null;
        // if (!this.animate) console.warn("Motion One not found — using lerp fallback.");

        // prepare DOM (create track and move items)
        this._prepareTrack();

        // bind
        this._onResize = this._onResize.bind(this);
        this._onScroll = this._onScroll.bind(this);
        this._onFrame = this._onFrame.bind(this);

        window.addEventListener('resize', this._onResize);
        window.addEventListener('scroll', this._onScroll, { passive: true });

        // <<< IMPORTANT: defer initial layout measurement so clone widths are correct
        this._deferInitialLayout();
    }

    _prepareTrack() {
        // create a track that will hold absolute-positioned items
        this.track = document.createElement('div');
        this.track.className = 'marquee-track';
        Object.assign(this.track.style, {
            position: 'absolute',
            left: '0px',
            top: '0px',
            height: '100%',
            width: '100%',
            pointerEvents: 'none', // enable pointer on items themselves if needed
        });

        // append track and move children from .marquee__inner into track
        this.root.appendChild(this.track);
        // Move all children from inner into track
        const nodes = Array.from(this.inner.children);
        nodes.forEach(node => this.track.appendChild(node));
        // keep inner hidden (we use absolute items)
        this.inner.style.visibility = 'hidden';

        // ensure container is positioned (for absolute children)
        if (getComputedStyle(this.root).position === 'static') {
            this.root.style.position = 'relative';
        }
    }

    // NEW: defer initial layout/measurement until browser lays out clones & images load
    _deferInitialLayout() {
        const rafDouble = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));

        // If there are images inside the items, wait until they load (otherwise sizes may be zero)
        const imgs = Array.from(this.track.querySelectorAll('img'));
        if (imgs.length === 0) {
            // run measurement after two rAFs and still do a small timeout fallback
            rafDouble(() => {
                this._onResize();
                // micro fallback in 120ms to catch late layout changes (fonts/images)
                setTimeout(() => this._onResize(), 120);
            });
            return;
        }

        // otherwise wait for images to finish loading (or error)
        let pending = imgs.filter(img => !img.complete);
        if (pending.length === 0) {
            rafDouble(() => {
                this._onResize();
                setTimeout(() => this._onResize(), 120);
            });
            return;
        }

        const onOneLoaded = () => {
            pending = pending.filter(i => !i.complete);
            if (pending.length === 0) {
                rafDouble(() => {
                    this._onResize();
                    setTimeout(() => this._onResize(), 120);
                });
            }
        };

        pending.forEach(img => {
            img.addEventListener('load', onOneLoaded, { once: true });
            img.addEventListener('error', onOneLoaded, { once: true });
        });

        // Safety net: if images hang for some reason, force layout after 500ms
        setTimeout(() => {
            if (pending.length > 0) {
                rafDouble(() => {
                    this._onResize();
                    setTimeout(() => this._onResize(), 120);
                });
            }
        }, 500);
    }

    _ensureEnoughClones() {
        // ensure the total width of items covers at least rootWidth * 2 (safe buffer)
        let total = this.sizes.reduce((s, w) => s + w + this.gap, 0);
        const rootW = this.root.clientWidth;
        const originalNodes = Array.from(this.track.children);
        // if total is zero (first time), sizes will be empty; we'll measure below
        let idx = 0;
        while (total < rootW * 2) {
            // clone the original set in round robin
            const original = originalNodes[idx % originalNodes.length];
            const clone = original.cloneNode(true);
            this.track.appendChild(clone);
            // measure size after appended
            const w = clone.offsetWidth || clone.getBoundingClientRect().width;
            total += w + this.gap;
            this.sizes.push(w);
            this.items.push(clone);
            idx++;
            // guard: break if infinite
            if (idx > 200) break;
        }
    }

    _onResize() {
        // Rebuild item lists and positions based on current DOM sizes.
        // Clear previous arrays
        this.items = Array.from(this.track.children);
        this.sizes = this.items.map(el => el.offsetWidth || el.getBoundingClientRect().width);

        // If we don't have enough clones, clone until we have enough width coverage
        let total = this.sizes.reduce((s, w) => s + w + this.gap, 0);
        const rootW = this.root.clientWidth;
        const original = Array.from(this.items);
        let cloneIndex = 0;
        while (total < rootW * 2) {
            const nodeToClone = original[cloneIndex % original.length];
            const clone = nodeToClone.cloneNode(true);
            this.track.appendChild(clone);
            const w = clone.offsetWidth || clone.getBoundingClientRect().width;
            this.items.push(clone);
            this.sizes.push(w);
            total += w + this.gap;
            cloneIndex++;
            if (cloneIndex > 300) break;
        }

        // Now compute initial positions sequentially from left to right
        this.positions = [];
        let x = 0;
        for (let i = 0; i < this.items.length; i++) {
            const el = this.items[i];
            // make item absolutely positioned and visually centered vertically
            Object.assign(el.style, {
                position: 'absolute',
                left: '0px',
                top: '50%',
                transform: `translateX(${x}px) translateY(-50%)`,
                willChange: 'transform',
                pointerEvents: 'auto',
            });
            this.positions[i] = x;
            // ensure sizes array correct
            this.sizes[i] = this.sizes[i] || el.offsetWidth || el.getBoundingClientRect().width;
            x += this.sizes[i] + this.gap;
        }

        // store total track length (useful for recycling end)
        this._trackLength = x;
    }

    _onScroll() {
        const now = performance.now();
        const y = window.scrollY;
        const dt = Math.max(1, now - this._lastScrollTs);
        const dy = y - this._lastScrollY;
        const vel = (dy / dt) * 1000; // px/sec

        this._lastScrollY = y;
        this._lastScrollTs = now;

        // smoothing measured velocity
        this._measuredVel = this._measuredVel * 0.6 + vel * 0.4;

        // --- Aggressive mapping ---
        const absVel = Math.min(Math.abs(this._measuredVel), this.clampVelTo);

        // Power curve — exaggerates changes
        const normalized = (absVel / this.clampVelTo) ** 0.5; // sqrt for stronger low-end boost

        // Make it much more noticeable
        const mapped = 1 + normalized * (this.maxMultiplier - 1);

        this.setTargetMultiplier(mapped);
    }

    setTargetMultiplier(v) {
        // never zero: keep minimum flow
        this._targetMultiplier = Math.max(0.05, v);
        if (this.animate) {
            // cancel old animation
            try { this._motionAnim?.cancel(); } catch (e) { }
            // animate numeric value using Motion One spring
            this._motionAnim = this.animate(
                { from: this._multiplier, to: this._targetMultiplier },
                {
                    type: 'spring',
                    stiffness: this.springConfig.stiffness,
                    damping: this.springConfig.damping,
                    onUpdate: (val) => { this._multiplier = val; },
                }
            );
        }
    }

    _onFrame(now) {
        const dt = Math.min(64, now - this._lastFrame) / 1000;
        this._lastFrame = now;

        if (!this.animate) {
            // fallback lerp smoothing
            this._multiplier += (this._targetMultiplier - this._multiplier) * Math.min(1, dt * 8);
        }

        // movement amount in px for this frame
        const frameMove = this.baseSpeed * this._multiplier * dt * this.direction;

        const rootW = this.root.clientWidth;
        // update positions and recycle when out of view
        for (let i = 0; i < this.items.length; i++) {
            let x = this.positions[i];
            // leftwards movement reduces x when direction === 1
            x -= frameMove;

            const w = this.sizes[i] || this.items[i].offsetWidth;

            // recycle left-to-right (normal: direction === 1)
            if (this.direction === 1 && (x + w) < -this.recycleBuffer) {
                // put it to the rightmost position
                const maxX = Math.max(...this.positions);
                x = maxX + (this.sizes[this.positions.indexOf(maxX)] || 0) + this.gap;
            }
            // recycle right-to-left (direction === -1)
            else if (this.direction === -1 && x > rootW + this.recycleBuffer) {
                const minX = Math.min(...this.positions);
                x = minX - w - this.gap;
            }

            this.positions[i] = x;
            // apply transform (keep vertical centering)
            this.items[i].style.transform = `translateX(${Math.round(x)}px) translateY(-50%)`;
        }

        // continue loop if playing
        if (this._playing) this._raf = requestAnimationFrame(this._onFrame);
    }

    start() {
        if (this._playing) return;
        this._playing = true;
        this._lastFrame = performance.now();
        this._raf = requestAnimationFrame(this._onFrame);
    }

    stop() {
        this._playing = false;
        if (this._raf) cancelAnimationFrame(this._raf);
        this._raf = null;
    }

    destroy() {
        this.stop();
        window.removeEventListener('resize', this._onResize);
        window.removeEventListener('scroll', this._onScroll);
        try { this._motionAnim?.cancel(); } catch (e) { }
        // optional: move items back to original inner or clear
    }
}
